import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';

const UploadFormsModal = () => {
  const {
    showUploadFormsModal,
    setShowUploadFormsModal,
    uploadFormsProject,
    showNotification,
    setProjects
  } = useAppContext();

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  if (!showUploadFormsModal || !uploadFormsProject) return null;

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      showNotification("No Files", "Please select files to upload", "warning");
      return;
    }

    setIsUploading(true);
    
    // Simulate upload without Firebase for now
    setTimeout(() => {
      // Update local state only
      const uploadedForms = selectedFiles.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
        uploadDate: new Date().toISOString()
      }));

      const deliverable = {
        title: description || "Document Upload",
        description: description || `${selectedFiles.length} file(s) uploaded`,
        files: selectedFiles.map(f => f.name),
        uploadDate: new Date().toISOString(),
        type: 'concept-design'
      };

      // Update local state
      setProjects(prev => prev.map(project => {
        if (project.id === uploadFormsProject.id) {
          return {
            ...project,
            uploadedForms: [...(project.uploadedForms || []), ...uploadedForms],
            deliverables: [...(project.deliverables || []), deliverable],
            communications: [
              ...(project.communications || []),
              {
                type: 'client',
                sender: project.client,
                message: `Uploaded ${selectedFiles.length} file(s)${description ? ': ' + description : ''}`,
                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
              }
            ]
          };
        }
        return project;
      }));

      setIsUploading(false);
      showNotification("Upload Complete", "Documents uploaded successfully", "success");
      
      // Clear and close
      setSelectedFiles([]);
      setDescription('');
      setShowUploadFormsModal(false);
    }, 1500);
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setDescription('');
    setShowUploadFormsModal(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full">
        <h2 className="text-xl font-bold mb-4">Upload Documents</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">Project</label>
          <div className="p-2 bg-gray-100 rounded text-sm">
            {uploadFormsProject.name} - Stage {uploadFormsProject.stage}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Concept design description"
            className="w-full p-2 border rounded"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">Files</label>
          <input
            type="file"
            multiple
            onChange={handleFileSelect}
            className="w-full p-2 border rounded"
            accept=".pdf,.doc,.docx,.jpg,.png"
          />
          
          {selectedFiles.length > 0 && (
            <div className="mt-2 space-y-1">
              {selectedFiles.map((file, idx) => (
                <div key={idx} className="flex justify-between items-center bg-gray-50 p-2 rounded text-sm">
                  <span>{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                  <button
                    onClick={() => removeFile(idx)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
            disabled={isUploading || selectedFiles.length === 0}
          >
            {isUploading ? "Uploading..." : "Upload Documents"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadFormsModal;